import { useNavigate, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import { NotFoundErrorScreen } from 'components/screens';
import { Text } from 'components/Text';
import { PATHS } from 'config/paths';
import { getTrans } from 'modules/i18n';
import {
  DateFormat,
  DEFAULT_TIMEZONE,
  formatDateWithTimezone,
  getHoursAndMinutesLabel
} from 'utils/date';
import { pressableSx } from 'utils/motion';
import { useWeekDetails } from '../hooks';
import { ShiftsSkeleton } from './ShiftsSkeleton';

export const Shifts = () => {
  const { weekId } = useParams();
  const { weekDetails, isLoading, isError } = useWeekDetails();
  const navigate = useNavigate();

  const shiftsIds = Object.keys(weekDetails?.shifts ?? {});

  if (isLoading) {
    return <ShiftsSkeleton />;
  }

  if (isError) {
    return (
      <NotFoundErrorScreen
        title={getTrans('weekDetailsPage.notFoundScreen.title')}
        description1={getTrans('weekDetailsPage.notFoundScreen.description1')}
        description2={getTrans('weekDetailsPage.notFoundScreen.description2')}
      />
    );
  }

  if (!weekDetails || !shiftsIds.length) return null;

  const assignment = weekId ? weekDetails.assignments[weekId] : undefined;
  // The Total line is the screen's anchor: the sum of logged shift hours vs planned.
  const totalWorkedMinutes = shiftsIds.reduce(
    (sum, id) => sum + (weekDetails.shifts[id]?.shiftReportedMinutes ?? 0),
    0
  );

  return (
    <Box>
      <Text
        variant="subtitle1"
        color="text.primary"
        sx={{ display: 'block', fontWeight: 700, mb: 1.5, ml: 0.5 }}
      >
        {getTrans('weekDetailsPage.shifts.title')}
      </Text>
      <Card
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: theme => theme.customShadows?.card ?? theme.shadows[1]
        }}
      >
        {shiftsIds.map((id, index) => {
          const { shiftStart, shiftReportedMinutes } = weekDetails.shifts[id];
          const day = formatDateWithTimezone(
            shiftStart,
            DateFormat.DAY,
            DEFAULT_TIMEZONE
          );
          const weekday = formatDateWithTimezone(
            shiftStart,
            DateFormat.SHORT_WEEKDAY,
            DEFAULT_TIMEZONE
          );
          const month = formatDateWithTimezone(
            shiftStart,
            DateFormat.SHORT_MONTH,
            DEFAULT_TIMEZONE
          );

          return (
            <Box
              key={`shift-${id}`}
              data-testid={`shift-card-${id}`}
              onClick={() =>
                navigate(`${PATHS.shiftDetails}`.replace(':shiftId', id))
              }
              sx={{
                ...pressableSx,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                px: 2.5,
                py: 1.75,
                // Match the weeks-overview ledger row height for cross-screen consistency.
                minHeight: 88,
                borderTop: index === 0 ? 'none' : '1px solid',
                borderColor: 'divider',
                // Real-pointer only, so the highlight doesn't stick after a tap on touch.
                '@media (hover: hover)': {
                  '&:hover': { bgcolor: 'action.hover' }
                }
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Text variant="subtitle2" color="text.primary">
                  {`${weekday}, ${day}. ${month}`}
                </Text>
              </Box>
              <Text variant="subtitle2" color="text.primary">
                {getHoursAndMinutesLabel(shiftReportedMinutes)}
              </Text>
            </Box>
          );
        })}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            px: 2.5,
            py: 2,
            borderTop: '2px solid',
            borderColor: 'divider'
          }}
        >
          <Text
            variant="subtitle1"
            color="text.primary"
            sx={{ fontWeight: 800 }}
          >
            {getTrans('weekDetailsPage.shifts.total')}
          </Text>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 800, color: 'text.primary' }}
            >
              {getHoursAndMinutesLabel(totalWorkedMinutes)}
            </Typography>
            {assignment && (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                / {getHoursAndMinutesLabel(assignment.expectedMinutes)}
              </Typography>
            )}
          </Box>
        </Box>
      </Card>
    </Box>
  );
};
